// Port of ios/Sources/ViewModels/WhatsAppChatForm.swift.
//
// The pure half of "new WhatsApp chat" from the "+" menu (shared/API.md §15.4): the same three
// things the bridge's `/#new` slash command takes — country code, ten-digit number, contact name —
// captured as fields instead of typed as one line, then executed as exactly that command.
//
// The bridge answers in two stages: an instant ephemeral ack, and up to a minute later either the
// new channel or a private note in the channel the command ran from. This type owns the parts of
// that conversation a test can pin: what the command line is, which channel the bridge will
// create, which ack means "working on it" and which note means "it failed". Android's
// `WhatsAppChatForm` is the twin.

export const defaultCountryCode = '52';
export const countryCodeDigits = 2;
export const nationalDigits = 10;
export const maxName = 64;
export const privateMarker = '-##private##-';

/** Swift's `trimmingCharacters(in: .whitespaces)`: spaces and tabs, not newlines. */
function trimSpaces(text: string): string {
  return text.replace(/^[\p{Zs}\t]+|[\p{Zs}\t]+$/gu, '');
}

/**
 * A team is a WhatsApp line when its commands include BOTH `/#new` and `/#save`. `#new` alone
 * is not enough: a retired Evolution-era team still carries a `#new` with a different syntax
 * (phone, title and first message), and only the Mac-backed lines also have `#save`.
 */
export function isWhatsAppLine(triggers: readonly string[]): boolean {
  const normalized = new Set(
    triggers.map((trigger) => {
      let value = trimSpaces(trigger);
      if (value.startsWith('/')) value = value.slice(1);
      return value.toLowerCase();
    }),
  );
  return normalized.has('#new') && normalized.has('#save');
}

export function digits(raw: string): string {
  return raw.replace(/[^0-9]/g, '');
}

/** One line: the bridge parses up to the end of the text, so a newline would end the name. */
export function cleanName(raw: string): string {
  const collapsed = raw
    .split(/\s+/u)
    .filter((w) => w !== '')
    .join(' ');
  // `prefix(maxName)` counts Characters; code points is the closest cheap equivalent.
  return trimSpaces([...collapsed].slice(0, maxName).join(''));
}

export function isSubmittable(countryCode: string, number: string, name: string): boolean {
  return (
    digits(countryCode).length === countryCodeDigits &&
    digits(number).length === nationalDigits &&
    cleanName(name) !== ''
  );
}

/** Country code + national number, the digits the bridge builds the JID from. */
export function fullNumber(countryCode: string, number: string): string {
  return digits(countryCode) + digits(number);
}

/** The exact line the slash command expects: `/#new 52 5512345678 Juan Pérez`. */
export function command(countryCode: string, number: string, name: string): string {
  return `/#new ${digits(countryCode)} ${digits(number)} ${cleanName(name)}`;
}

/**
 * The slug of the channel the bridge creates for this number — `<digits>@s.whatsapp.net` with
 * `@` and `.` turned into dashes, prefixed `wa-`. Deterministic, which is what lets the client
 * find the conversation without the bridge telling it.
 */
export function channelName(countryCode: string, number: string): string {
  return `wa-${fullNumber(countryCode, number)}-s-whatsapp-net`;
}

/**
 * The bridge's acks that mean work is under way (`⏳ Verificando…`, `⏳ Ya se está
 * procesando…`). Any other ephemeral text is a refusal (usage, wrong team, not authorised).
 */
export function isProgressAck(text: string | null | undefined): boolean {
  if (text == null) return true;
  return text.trim().startsWith('⏳');
}

/**
 * The bridge's failure note for THIS number, or null. Its outcome notes are real posts ending
 * in `privateMarker` (never relayed to WhatsApp). A success note starts with ✅ and is not a
 * failure; every failure but one names `+<digits>`, and the one that does not ("the contact
 * was saved but the channel could not be created") is matched by its opening words.
 */
export function failureNote(message: string, fullNumber: string): string | null {
  if (!message.includes(privateMarker)) return null;
  const text = message.replaceAll(privateMarker, '').trim();
  if (text.startsWith('✅')) return null;
  const aboutThisNumber = text.includes(`+${fullNumber}`) || text.startsWith('El contacto se guardó');
  return aboutThisNumber ? text : null;
}

/** A channel the command can run from: one the reader belongs to in this team. */
export interface ReplyCandidate {
  channelId: string;
  lastViewedAt: number;
}

/**
 * Where the command runs — which is also where the bridge posts its outcome note. The open
 * conversation when it belongs to the team (that is where the slash command would have been
 * typed), otherwise the one the reader looked at last.
 */
export function replyChannel(selectedChannelId: string | null | undefined, candidates: readonly ReplyCandidate[]): string | null {
  if (selectedChannelId != null && candidates.some((c) => c.channelId === selectedChannelId)) {
    return selectedChannelId;
  }
  let best: ReplyCandidate | null = null;
  for (const c of candidates) {
    // Swift's `max(by:)` keeps the LAST of equal maxima.
    if (best == null || !(c.lastViewedAt < best.lastViewedAt)) best = c;
  }
  return best?.channelId ?? null;
}
